import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { ThemeEvents } from '@theme/events';

class DiffyCartUpsell extends HTMLElement {
  #isSubmitting = false;
  #activeButton = null;
  #lastQuantityInteraction = 0;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityInteraction);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityInteraction);
  }

  #handleQuantityInteraction = () => {
    /*
     * IMPORTANTE: o Horizon aplica debounce antes de atualizar a quantidade.
     * Guardamos o instante do clique para impedir que o upsell ultrapasse essa atualização.
     */
    this.#lastQuantityInteraction = performance.now();
  };

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('[data-diffy-upsell-add]');
    if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;

    event.preventDefault();
    this.#handleAdd(button);
  };

  async #waitForCartToBecomeIdle() {
    const horizonDebounceBuffer = 380;
    const maximumWait = 10000;
    const startedAt = performance.now();

    while (performance.now() - startedAt < maximumWait) {
      const timeSinceQuantityInteraction = performance.now() - this.#lastQuantityInteraction;
      const cartIsUpdating = Array.from(document.querySelectorAll('cart-items-component')).some(
        (component) => component.classList.contains('cart-items-disabled')
      );

      if (timeSinceQuantityInteraction >= horizonDebounceBuffer && !cartIsUpdating) {
        return;
      }

      await new Promise((resolve) => window.setTimeout(resolve, 50));
    }

    throw new Error('O carrinho ainda está atualizando. Aguarde um instante e tente novamente.');
  }

  async #handleAdd(button) {
    /* Trava imediata: dois toques no mesmo upsell geram somente uma inclusão. */
    if (this.#isSubmitting) return;

    const variantId = Number(button.dataset.variantId);
    if (!Number.isInteger(variantId) || variantId <= 0) {
      this.#showError('Não foi possível identificar a variante complementar.');
      return;
    }

    this.#isSubmitting = true;
    this.#activeButton = button;
    this.#setButtonLoading(button, true);

    try {
      /*
       * Não altere esta ordem: primeiro termina a mudança de quantidade da Case;
       * somente depois o complemento é enviado ao Shopify.
       */
      await this.#waitForCartToBecomeIdle();

      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((component) => (component instanceof HTMLElement ? component.dataset.sectionId : ''))
        .filter(Boolean);

      const deferredEventPromise = CartLinesUpdateEvent.createPromise();
      const eventTarget = this.isConnected ? this : document.querySelector('diffy-cart-upsell');

      if (!(eventTarget instanceof HTMLElement)) {
        throw new Error('A gaveta foi atualizada. Toque novamente em adicionar.');
      }

      eventTarget.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'cart',
          lines: [
            {
              merchandiseId: variantId.toString(),
              quantity: 1,
            },
          ],
          promise: deferredEventPromise.promise,
        })
      );

      try {
        const response = await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections: [...new Set(sectionIds)].join(','),
            sections_url: window.location.pathname,
          }),
        });

        const result = await response.json();

        if (!response.ok || result.status || result.errors) {
          throw new Error(result.description || result.message || 'Não foi possível adicionar este item.');
        }

        const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        });

        if (!cartResponse.ok) {
          throw new Error('O item foi adicionado, mas o carrinho não pôde ser atualizado.');
        }

        const cart = await cartResponse.json();

        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            sections: result.sections,
            items: cart.items,
            itemCount: cart.item_count,
            productId: button.dataset.productId,
            source: 'diffy-cart-upsell',
            sourceId: this.id || 'diffy-cart-upsell',
            didError: false,
          },
        });

        const status = eventTarget.querySelector('[data-diffy-upsell-status]');
        if (status) status.textContent = 'Complemento adicionado ao carrinho.';

        window.setTimeout(() => this.#resetSubmittingState(), 1200);
      } catch (error) {
        deferredEventPromise.reject(error);
        throw error;
      }
    } catch (error) {
      this.#showError(error instanceof Error ? error.message : 'Não foi possível adicionar este item.');

      const errorTarget = this.isConnected ? this : document.querySelector('diffy-cart-upsell');
      errorTarget?.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Falha ao adicionar ao carrinho',
          code: 'SERVICE_UNAVAILABLE',
        })
      );

      this.#resetSubmittingState();
    }
  }

  #setButtonLoading(button, isLoading) {
    button.classList.toggle('is-loading', isLoading);
    button.disabled = isLoading;
    button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }

  #resetSubmittingState() {
    if (this.#activeButton?.isConnected) {
      this.#setButtonLoading(this.#activeButton, false);
    }

    this.#activeButton = null;
    this.#isSubmitting = false;
  }

  #showError(message) {
    const currentUpsell = this.isConnected ? this : document.querySelector('diffy-cart-upsell');
    if (!(currentUpsell instanceof HTMLElement)) return;

    currentUpsell.classList.add('has-error');

    const status = currentUpsell.querySelector('[data-diffy-upsell-status]');
    if (status) status.textContent = message;

    window.setTimeout(() => {
      currentUpsell.classList.remove('has-error');
    }, 6000);
  }
}

if (!customElements.get('diffy-cart-upsell')) {
  customElements.define('diffy-cart-upsell', DiffyCartUpsell);
}
